import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type Ref,
} from 'react'
import { cx, focusablesIn, isFilled, useControllableState, useMediaQuery } from '@/shared/lib'
import { ScrollToTop } from '@/shared/ui/ScrollToTop'
import s from './AppShell.module.css'

type AppShellOwnProps = {
  /**
   * Текст ссылки «Перейти к содержимому». Обязателен: забыть её — значит заставить
   * пользователя клавиатуры обходить всю навигацию на каждой странице.
   */
  skipToContentLabel: string
  /** Содержимое страницы. Едет в `<main>`. */
  children?: ReactNode
  /**
   * Шапка приложения (`AppTopBar`). Прилипает и приносит ориентир `banner` сама —
   * каркас её не оборачивает, а только измеряет: от её высоты считается верх липкой
   * колонки навигации.
   */
  header?: ReactNode
  /** Содержимое подвала (`contentinfo`). Элемент `<footer>` рисует каркас. */
  footer?: ReactNode
  /**
   * Плавающая полоса у нижнего края (`FloatingActionBar`). Монтируется прямым потомком
   * корня: `backdrop-filter` любого предка сломал бы её `position: fixed`. Пока слот
   * заполнен, кнопка «наверх» не монтируется — решение 6 контракта.
   */
  bottomBar?: ReactNode
  /** Имя кнопки «наверх». Не задано — кнопки нет вовсе. */
  scrollToTopLabel?: string
  /** Открыта ли выезжающая панель. Кнопка-бургер живёт в шапке, то есть снаружи. */
  navOpen?: boolean
  defaultNavOpen?: boolean
  /** Просьба открыть или закрыть: бургер, скрим, Escape, выбор пункта, порог 1024px. */
  onNavOpenChange?: (open: boolean) => void
  /** Ведёт на корень кадра. */
  ref?: Ref<HTMLDivElement>
}

/**
 * Навигация без имени не собирается: ниже 1024px панель становится диалогом, и безымянный
 * диалог скринридер объявляет как «диалог». Union запрещает такую сборку компилятором,
 * а не подписью в контракте — тот же приём, что у `heading`/`label` в `Modal`.
 */
type AppShellNavProps =
  | {
      /** Навигация (`AppSidebar`): с 1024px липкая колонка, ниже — выезжающая панель. */
      sidebar: ReactNode
      /** Имя панели для скринридера: «Разделы системы». */
      navLabel: string
    }
  | { sidebar?: undefined; navLabel?: never }

export type AppShellProps = AppShellOwnProps &
  AppShellNavProps &
  Omit<HTMLAttributes<HTMLDivElement>, 'children'>

/**
 * Порог, с которого навигация становится колонкой. `lg` из шкалы брейкпоинтов проекта.
 * Значение продублировано в модуле стилей: CSS умеет переложить панель, но не умеет снять
 * с неё роль диалога, а container queries вне планки браузеров (Safari 16).
 */
const DESKTOP_QUERY = '(min-width: 1024px)'

/**
 * Каркас рабочего приложения: кадр страницы, липкая шапка, колонка навигации, содержимое,
 * подвал и нижний край экрана. Единственный компонент, отвечающий на вопрос «где на экране что»;
 * всё остальное живёт внутри его слотов и меняется от экрана к экрану, а каркас — нет.
 *
 * Прокручивается **окно**: областей прокрутки каркас не создаёт вообще, иначе на мобильном
 * не прячется адресная строка, ломается восстановление позиции и `position: sticky` внутри
 * содержимого начинает липнуть к чужому краю. Отсюда же требование к потребителю: `overflow`
 * на предках каркаса не ставится — липкая шапка и колонка молча перестанут работать.
 *
 * Ниже 1024px колонка навигации становится выезжающей панелью со скримом. Узел при этом
 * **тот же самый**: `AppSidebar` о телефоне не знает, его состояние переживает поворот экрана,
 * а в DOM нет двух копий навигации с двойным обходом по Tab.
 *
 * Контракт: docs/ui-kit/contracts/app-shell.md
 */
export function AppShell({
  skipToContentLabel,
  children,
  header,
  sidebar,
  navLabel,
  footer,
  bottomBar,
  scrollToTopLabel,
  navOpen,
  defaultNavOpen = false,
  onNavOpenChange,
  className,
  ref,
  ...rest
}: AppShellProps) {
  const generatedId = useId()
  // useId отдаёт разделители (:r0:), а они попадают во фрагмент ссылки «к содержимому»
  const mainId = `app-shell-main-${generatedId.replace(/[^a-zA-Z0-9_-]/g, '')}`

  const [open, setOpen] = useControllableState(navOpen, defaultNavOpen, onNavOpenChange)
  const isDesktop = useMediaQuery(DESKTOP_QUERY)

  const rootRef = useRef<HTMLDivElement | null>(null)
  const headerSlotRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const mainRef = useRef<HTMLElement | null>(null)
  /** Кто открыл панель: бургер в шапке. Ему же возвращается фокус при закрытии. */
  const openerRef = useRef<HTMLElement | null>(null)

  // Пустым считается и boolean: идиома `can('nav.view') && <AppSidebar/>` из правила 8
  const hasHeader = isFilled(header)
  const hasSidebar = isFilled(sidebar)
  const hasBottomBar = isFilled(bottomBar)

  /** Панель вместо колонки. Роль диалога и ловушка фокуса включаются только здесь. */
  const drawer = hasSidebar && !isDesktop
  const drawerOpen = drawer && open

  /** Корень нужен и снаружи (потребителю), и внутри (переменная высоты шапки). */
  const attachRoot = useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node
      if (ref !== null && typeof ref === 'object') {
        ref.current = node
        return undefined
      }
      if (typeof ref !== 'function') return undefined

      /*
       * ref-колбэк потребителя в React 19 вправе вернуть свой cleanup, и вернуть его наружу
       * как есть нельзя: увидев функцию, React перестаёт звать attachRoot(null) при отвязке,
       * и rootRef остался бы на оторванном узле. Возвращается своя очистка — она гасит
       * ссылку и зовёт чужую.
       */
      const cleanup = ref(node)
      return () => {
        rootRef.current = null
        if (typeof cleanup === 'function') cleanup()
      }
    },
    [ref],
  )

  /*
   * Экран стал широким — панели больше нет, и открытой она числиться не должна: вернувшись
   * на узкий, пользователь обнаружил бы меню, которого не открывал.
   */
  useEffect(() => {
    if (isDesktop && open) setOpen(false)
  }, [isDesktop, open, setOpen])

  /*
   * Сколько сверху занимает прилипшая шапка: её собственный отступ плюс высота. Кит на этом
   * месте зашивает top: 132px (Screens 472) — число, верное ровно для одной ширины окна.
   * Шапка переносит ряды, поэтому величина читается с живого узла и кладётся переменной
   * на корень: от неё считаются верх липкой колонки, её потолок высоты и scroll-margin
   * у <main>. Положение читается вместе с высотой: нелипкая шапка (печать, вложенная
   * раскладка) верх кадра не занимает, и колонка обязана подняться к полю.
   */
  useLayoutEffect(() => {
    const root = rootRef.current
    const headerNode = headerSlotRef.current?.firstElementChild
    if (!root) return

    if (!(headerNode instanceof HTMLElement)) {
      root.style.removeProperty('--app-shell-header-rail')
      return
    }

    const measure = () => {
      const style = window.getComputedStyle(headerNode)
      const stuck = style.position === 'sticky'
      const offset = stuck ? Number.parseFloat(style.top) || 0 : 0
      const rail = stuck ? offset + headerNode.offsetHeight : 0
      root.style.setProperty('--app-shell-header-rail', `${rail}px`)
    }

    measure()

    // В jsdom ResizeObserver не реализован — отсутствие API не должно ронять тесты (приём Popover)
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(measure)
    observer.observe(headerNode)
    return () => observer.disconnect()

    /*
     * header в зависимостях, а не только hasHeader: узел в слоте вправе смениться (ленивая
     * шапка, другой компонент по маршруту), а ResizeObserver будит measure лишь на изменение
     * размера — position и top он не наблюдает. isDesktop — потому что липкость снимается
     * медиазапросом, и на смене раскладки рейку надо перечитать.
     */
  }, [hasHeader, header, isDesktop])

  /*
   * Фокус: при открытии уезжает на саму панель (скринридер объявляет «Разделы системы, диалог»,
   * а не первый пункт), при закрытии возвращается открывателю. Открыватель мог исчезнуть вместе
   * с рядом шапки на узком экране — отсюда проверка isConnected.
   */
  useEffect(() => {
    if (!drawerOpen) return

    const active = document.activeElement
    // body в открыватели не годится: вернуть ему фокус — то же самое, что не возвращать никому
    openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null

    panelRef.current?.focus({ preventScroll: true })

    return () => {
      const opener = openerRef.current
      openerRef.current = null
      if (opener?.isConnected === true) opener.focus({ preventScroll: true })
    }
  }, [drawerOpen])

  /*
   * Escape закрывает панель. Условие «цель внутри панели» разводит случай с модалкой, открытой
   * поверх меню: фокус тогда в карточке, и Escape закрывает её, а не панель под ней.
   * Всплытие, а не перехват: содержимое вправе обработать Escape раньше и погасить событие.
   */
  useEffect(() => {
    if (!drawerOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return

      const panel = panelRef.current
      const target = event.target
      const inside = panel !== null && target instanceof Node && panel.contains(target)
      // Фокус потерян (возврат из адресной строки): отвечает панель — она верхний слой
      const nowhere = target === document.body || target === document.documentElement

      if (!inside && !nowhere) return
      setOpen(false)
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [drawerOpen, setOpen])

  /** Ловушка фокуса: Tab по кругу внутри панели, иначе он уходит на ссылки под скримом. */
  const handlePanelKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const panel = panelRef.current
    if (!drawerOpen || event.key !== 'Tab' || event.defaultPrevented || !panel) return

    const focusables = focusablesIn(panel)
    const active = document.activeElement

    if (focusables.length === 0) {
      // Фокусировать внутри нечего: Tab не имеет права увести на страницу под скримом
      event.preventDefault()
      panel.focus({ preventScroll: true })
      return
    }

    const first = focusables[0]
    const last = focusables[focusables.length - 1]

    // Вперёд с последнего — на первый; назад с первого (и с самой панели) — на последний
    if (!event.shiftKey && active === last && first) {
      event.preventDefault()
      first.focus()
      return
    }

    if (event.shiftKey && (active === first || active === panel) && last) {
      event.preventDefault()
      last.focus()
    }
  }

  /*
   * Выбран раздел — панель закрывается: каркас о роутере ничего не знает и о переходе иначе
   * не узнает, а меню, оставшееся поверх новой страницы, пользователь закрывает руками.
   * Ссылка, а не любой клик: кнопки-аккордеоны групп разделов панель не закрывают.
   */
  const handlePanelClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!drawerOpen) return

    const target = event.target
    if (target instanceof Element && target.closest('a[href]') !== null) setOpen(false)
  }

  /*
   * Нажатие, а не клик: click приходит после отпускания и срабатывает на общем предке, если
   * выделение началось в панели, а закончилось на скриме. Тот же довод, что у «клика вне»
   * в Popover и у скрима Modal.
   */
  const handleScrimPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return
    setOpen(false)
  }

  const navState = !hasSidebar ? 'none' : isDesktop ? 'static' : open ? 'open' : 'closed'

  return (
    <div {...rest} ref={attachRoot} className={cx(s.root, className)} data-nav={navState}>
      {/* Первая остановка Tab на странице. Видна только в фокусе */}
      <a className={s.skipLink} href={`#${mainId}`}>
        {skipToContentLabel}
      </a>

      {/* display: contents — узел шапки остаётся прямым ребёнком кадра и липнет сам,
          а каркасу нужен только доступ к нему для измерения высоты */}
      {hasHeader ? (
        <div className={s.headerSlot} ref={headerSlotRef}>
          {header}
        </div>
      ) : null}

      <div className={s.body}>
        {hasSidebar ? (
          <>
            <div
              className={cx(s.scrim, drawerOpen && s.scrimOpen)}
              aria-hidden="true"
              onPointerDown={handleScrimPointerDown}
            />

            {/* Тот же узел в обоих режимах: колонка с 1024px, панель ниже. Роль диалога
                снимается вместе с режимом — CSS так не умеет, поэтому порог читается в JS */}
            <div
              ref={panelRef}
              className={cx(s.sidebar, drawerOpen && s.sidebarOpen)}
              role={drawerOpen ? 'dialog' : undefined}
              aria-modal={drawerOpen ? true : undefined}
              aria-label={drawerOpen ? navLabel : undefined}
              tabIndex={drawerOpen ? -1 : undefined}
              onKeyDown={handlePanelKeyDown}
              onClick={handlePanelClick}
            >
              {sidebar}
            </div>
          </>
        ) : null}

        {/* tabIndex={-1}: цель ссылки «к содержимому» и цель фокуса после прыжка «наверх» */}
        <main id={mainId} ref={mainRef} className={s.main} tabIndex={-1}>
          {children}
        </main>
      </div>

      {isFilled(footer) ? <footer className={s.footer}>{footer}</footer> : null}

      {bottomBar}

      {/*
       * Пока полоса действий показана, кнопки «наверх» нет: полоса перекрывает правый нижний
       * угол на любой ширине окна меньше ~1316px, а её высота каркасу неизвестна — она приходит
       * слотом и сама fixed. Разбор и отвергнутая альтернатива с --scroll-to-top-lift —
       * в контракте, решение 6.
       */}
      {scrollToTopLabel !== undefined && !hasBottomBar ? (
        <ScrollToTop label={scrollToTopLabel} focusTargetRef={mainRef} />
      ) : null}
    </div>
  )
}
